import { useSuspenseQuery } from "@apollo/client/react";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { useShowValidationErrors } from "~/common/hooks";
import type { MutationRefetchQueries } from "~/common/types";
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Label,
  SearchableMultiSelect,
} from "~/components/ui";
import { PropertySchemaAssociationsList, usePropertySchemaAssociationsForm } from "~/features/admin/propertySchemas";
import { useDataImportProjectsModel } from "~/features/dataImportProjects/context";
import {
  GetDataSourceForEditFormDocument,
  type GetDataSourceForEditFormQuery,
  type GetDataSourceForEditFormQueryVariables,
} from "~/features/dataImportProjects/gqls";
import { useDataSourceEdit } from "~/features/dataImportProjects/hooks/useDataSourceEdit";
import { usePropertySchemaCsvUpload } from "~/features/dataImportProjects/hooks/usePropertySchemaCsvUpload";
import {
  type DataSourceEditFormData,
  EvidencePropertySchemaTypes,
  zDataSourceEditFormDataKeys,
} from "~/features/dataImportProjects/types";
import { useTenant } from "~/features/tenant/context";

export const DATA_SOURCE_EDIT_FORM_ID = "data-source-edit-form";

interface DataSourceEditFormProps {
  onSubmit: () => void;
  dataSourceId: string;
  refetchQueriesOnEdit: MutationRefetchQueries;
}

export function DataSourceEditForm({ onSubmit, refetchQueriesOnEdit, dataSourceId }: DataSourceEditFormProps) {
  const { t } = useTranslation();
  const { tenantId } = useTenant();
  if (!tenantId) {
    throw new Error("Tenant ID is required");
  }
  const { references } = useDataImportProjectsModel();
  const csvInputRef = useRef<HTMLInputElement>(null);

  const { data } = useSuspenseQuery<GetDataSourceForEditFormQuery, GetDataSourceForEditFormQueryVariables>(
    GetDataSourceForEditFormDocument,
    {
      variables: { tenantId, id: dataSourceId },
    },
  );
  const dataSource = data?.dataSources?.[0];

  const methods = useForm<DataSourceEditFormData>({
    defaultValues: {
      name: dataSource?.name ?? "",
      references:
        dataSource?.dataSourceReferences.map((dr) => ({ id: dr.reference.id, title: dr.reference.title })) ?? [],
    },
  });
  const { handleSubmit, control, setError } = methods;
  const showValidationErrors = useShowValidationErrors(setError, zDataSourceEditFormDataKeys);

  const { submit } = useDataSourceEdit({
    onSubmit,
    dataSourceId,
    refetchQueriesOnEdit,
    onError: (error) => {
      showValidationErrors(error.fields);
    },
  });

  const propertySchemaAssociations = usePropertySchemaAssociationsForm(control);

  const showCsvUploadResult = useCallback((message: string, severity: "error" | "success") => {
    if (severity === "error") {
      toast.error(message);
      return;
    }
    toast.success(message);
  }, []);

  const { handlePropertySchemaCsvUpload } = usePropertySchemaCsvUpload({
    propertySchemaAssociations,
    showSnackbar: showCsvUploadResult,
    t,
  });

  useEffect(() => {
    const associations = dataSource?.dataSourcePropertySchemaSet[0]?.propertySchemaSet.propertySchemaAssociations ?? [];
    propertySchemaAssociations.setDefaults(associations);
  }, [data]);

  // A reference already linked to the data source may be absent from the project's
  // reference list; merge both so its chip keeps rendering instead of being dropped.
  const referenceOptions = useMemo(() => {
    const byId = new Map(references.map((reference) => [reference.id, reference.title]));
    for (const { reference } of dataSource?.dataSourceReferences ?? []) {
      if (!byId.has(reference.id)) {
        byId.set(reference.id, reference.title);
      }
    }
    return [...byId].map(([id, title]) => ({ id, label: title }));
  }, [references, dataSource?.dataSourceReferences]);

  if (!dataSource) {
    throw Error("Data source does not exist!");
  }

  const csvUploadLabel = t("feature.dataImportProjects.form.uploadPropertySchema");
  const csvUploadDisabled = propertySchemaAssociations.fields.length > 0;

  return (
    <Form {...methods}>
      <form id={DATA_SOURCE_EDIT_FORM_ID} onSubmit={handleSubmit(submit)} className="space-y-6 pt-3">
        <FormField
          control={control}
          name="name"
          rules={{ required: t("ui.required") }}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("model.common.name")}</FormLabel>
              <FormControl>
                <Input value={field.value} onChange={field.onChange} onBlur={field.onBlur} ref={field.ref} required />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="references"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("model.reference.modelNames")}</FormLabel>
              <FormControl>
                <SearchableMultiSelect
                  placeholder={t("model.reference.modelNames")}
                  searchPlaceholder={t("ui.search")}
                  emptyText={t("feature.common.noData")}
                  options={referenceOptions}
                  selectedIds={(field.value ?? []).map((reference: { id: string }) => reference.id)}
                  onChange={(ids) =>
                    field.onChange(
                      ids.map((id) => ({
                        id,
                        title: referenceOptions.find((option) => option.id === id)?.label ?? id,
                      })),
                    )
                  }
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="space-y-2">
          <PropertySchemaAssociationsList
            title={<h3 className="text-14-bold text-fg-1">{t("model.propertySchema.modelNames")}</h3>}
            fields={propertySchemaAssociations.fields}
            onRemove={propertySchemaAssociations.remove}
            onMove={propertySchemaAssociations.move}
            supportedPropertyTypes={EvidencePropertySchemaTypes}
          />
        </div>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={propertySchemaAssociations.append}>
            {t("feature.dataImportProjects.form.addPropertySchema")}
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={csvUploadDisabled}
            onClick={() => csvInputRef.current?.click()}
          >
            {csvUploadLabel}
          </Button>
          <Label htmlFor="data-source-property-schema-csv" className="sr-only">
            {csvUploadLabel}
          </Label>
          <input
            id="data-source-property-schema-csv"
            ref={csvInputRef}
            type="file"
            className="sr-only"
            tabIndex={-1}
            accept=".csv,text/csv,application/vnd.ms-excel"
            onChange={handlePropertySchemaCsvUpload}
          />
        </div>
      </form>
    </Form>
  );
}
